import { createFileRoute } from '@tanstack/react-router'

import type { Supplier } from '@/api/admin-inventory-types'
import { useUpdateSupplier } from '@/api/api-mutations'
import { preloadGetSupplier } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { getCurrencyOptions } from '../../-currency-utils'

export const Route = createFileRoute('/_authenticated/_general/inventory/suppliers/$supplierUuid/edit')({
	component: EditSupplierRoute,
	loader: async ({ context, params }) => {
		await preloadGetSupplier(context, { path: { supplierUuid: params.supplierUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.EditSupplier()
	}
})

function EditSupplierRoute() {
	const { supplierUuid } = Route.useParams()

	return (
		<RouteModal
			asDrawer
			prev='/inventory/suppliers'
			requiresData
			loader={async api => api.getSupplier({ path: { supplierUuid } })}
		>
			{supplier => <EditSupplierForm supplier={supplier} />}
		</RouteModal>
	)
}

function EditSupplierForm({ supplier }: { supplier: Supplier }) {
	const { L, locale } = useI18n()
	const { handleSuccess } = useRouteModal()
	const updateSupplier = useUpdateSupplier(true)

	const form = useAppForm({
		defaultValues: {
			name: supplier.name,
			email: supplier.email ?? '',
			currencyCode: supplier.currencyCode
		},
		onSubmit: async ({ value }) => {
			if (!value.currencyCode) {
				toast.error(L.General.Required())
				return
			}
			await updateSupplier.mutateAsync({
				path: { supplierUuid: supplier.uuid },
				body: {
					name: value.name,
					email: value.email || undefined,
					currencyCode: value.currencyCode
				}
			})
			toast.success(L.Inventory.EditSupplierSuccess())
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.EditSupplier()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField label={L.Inventory.Column.Name()} autoFocus />}
						/>
						<form.AppField
							name='email'
							children={field => <field.TextField label={L.Inventory.Column.Email()} optional />}
						/>
						<form.AppField
							name='currencyCode'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.SelectField
									label={L.Inventory.Column.Currency()}
									options={getCurrencyOptions(locale)}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small' type='button'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
